// CMS Frontend Types
export type SourceType = 'book' | 'articles' | 'website' | 'database' | 'jivan charitra';
export type IngestSourceType = 'indesign-html' | 'pagemaker' | 'word file' | 'database' | 'excel';
export type LanguageType = 'English' | 'Gujarati' | 'Hindi';
export type ContentStatus = 'draft' | 'ready_for_review' | 'ethics_review' | 'ethics_review_in_process' | 'ethics_reviewed' | 'entity_extraction_in_process' | 'entity_reviewed' | 'published';
export type VisibilityType = 'public' | 'karyakar' | 'sant' | 'hidden';
export type ReviewStatusType = 'none' | 'escalate' | 'resolved';
export type UserRole = 'superadmin' | 'admin' | 'editor' | 'reviewer' | 'editor-reviewer';

// User interface
export interface User {
    id: string;
    email: string;
    name: string;
    role: UserRole;
    browser?: boolean;
    parent?: User | null;
    parentId?: string | null;
    children?: User[];
    createdAt: Date;
    updatedAt: Date;
}

// Auth State interface
export interface AuthState {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    timezone: string;
}

// Content Source interface
export interface ContentSource {
    id: string;
    title: string;
    source: SourceType;
    ingest_source?: IngestSourceType;
    status: ContentStatus;
    filter_tags: string[];
    visibility: VisibilityType;
    ref_visibility: VisibilityType;
    review_status: ReviewStatusType;
    metadata: Record<string, any> | null;
    process_metadata?: Record<string, any> | null;
    sarangpur_path?: string | null;
    version: number; // For optimistic locking
    created_by: User;
    created_by_id: string;
    updated_by?: User;
    updated_by_id?: string;
    reviewed_by?: User;
    reviewed_by_id?: string;
    published_by?: User;
    published_by_id?: string;
    assigned_to?: User;
    assigned_to_id?: string;
    created_at: Date;
    updated_at: Date;
    reviewed_at?: Date;
    published_at?: Date;
    sections?: Section[];
    section_count?: number;
}

// Section interface
export interface Section {
    id: string;
    content_source_id: string;
    content_source?: ContentSource;
    parent_id?: string;
    parent?: Section;
    title: string;
    order_index: number;
    document_order?: number | null;
    level?: number;
    path: string;
    content_json: Record<string, any> | null;
    content_reviewed_json: Record<string, any> | null;
    filter_tags: string[];
    visibility?: VisibilityType;
    ref_visibility: VisibilityType;
    review_status: ReviewStatusType;
    metadata: Record<string, any> | null;
    reviewed_by?: User;
    reviewed_by_id?: string;
    updated_at: Date;
    version: number;
    reviewed_at?: Date;
    children?: Section[];
    depth?: number;
    computed_visibility?: VisibilityType;
    final_content?: Record<string, any> | null;
    has_children?: boolean;
    is_root?: boolean;
    breadcrumb_path?: string[];
    parent_path?: string | null;
}

// Section Entity Extraction (Feature) interface
export type FeatureType = 'ORIGINAL' | 'ORIGINAL_ELABORATED' | 'ENGLISH_SUMMARY' | 'GUJARATI_SUMMARY' | 'THEME' | 'ENGLISH_TRANSLATION' | 'MOCK_QUERY';
export type ExtractionStatus = 'pending' | 'approved' | 'rejected';
export type SQSSentStatus = 'pending' | 'sent' | 'failed';
export type PriorityLevel = 'IMMEDIATE' | 'NEXT_UPDATE';

export interface SectionEntityExtraction {
    id: string;
    section_id: string;
    section?: Section;
    feature_type: FeatureType;
    version: number;
    feature_data: {
        content: string;
        type: 'MARKDOWN_TEXT';
        contentHash: string;
        contentModifiedDateTime: Date;
    };
    feature_metadata?: Array<{
        type: string;
        key: string;
        value: any;
    }> | null;
    content_hash?: string;
    metadata_hash?: string;
    confidence_score?: number;
    status: ExtractionStatus;
    is_latest: boolean;
    extracted_at: Date;
    extracted_by?: User;
    extracted_by_id?: string;
    reviewed_at?: Date;
    reviewed_by?: User;
    reviewed_by_id?: string;
    sqs_message_id?: string;
    sqs_sent_at?: Date;
    sqs_sent_status?: SQSSentStatus;
    sqs_error_message?: string;
    priority_level: PriorityLevel;
    created_at: Date;
    updated_at: Date;
}

// Section tree node for hierarchical display
export interface SectionNode extends Section {
    children?: SectionNode[];
    expanded?: boolean;
    loading?: boolean;
}

// API Response types
export interface PaginationInfo {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    limit: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

export interface SortingInfo {
    sortBy: string;
    sortOrder: 'ASC' | 'DESC';
}

export interface FilterInfo {
    search?: string;
    status?: string;
    filter_tags?: string;
    assigned_to_id?: string;
    ref_visibility?: string;
    review_status?: string;
}

export interface ContentSourcesResponse {
    data: ContentSource[];
    pagination: PaginationInfo;
    sorting: SortingInfo;
    filters: FilterInfo;
}

export interface SectionsResponse {
    data: Section[];
}

export interface AutoSaveResponse {
    version: number;
    updated_at: Date;
    status: 'saved';
}

// Form types
export interface CreateContentSourceForm {
    title: string;
    source: SourceType;
    ingest_source?: IngestSourceType;
    filter_tags: string[];
    visibility: VisibilityType;
    ref_visibility: VisibilityType;
    metadata: Record<string, any>;
}

export interface UpdateContentSourceForm {
    title?: string;
    source?: SourceType;
    status?: ContentStatus;
    filter_tags?: string[];
    visibility?: VisibilityType;
    ref_visibility?: VisibilityType;
    review_status?: ReviewStatusType;
    metadata?: Record<string, any>;
    process_metadata?: Record<string, any>;
    content?: Record<string, any>; // Content to sync to sections (replaces raw_content)
    is_review_mode?: boolean;
}

export interface CreateSectionForm {
    title: string;
    parent_id?: string;
    order_index?: number;
    content_json?: Record<string, any>;
    filter_tags?: string[];
    visibility?: VisibilityType;
    ref_visibility?: VisibilityType;
    metadata?: Record<string, any>;
}

export interface UpdateSectionForm {
    title?: string;
    content_json?: Record<string, any>;
    content_reviewed_json?: Record<string, any>;
    filter_tags?: string[];
    visibility?: VisibilityType;
    ref_visibility?: VisibilityType;
    review_status?: ReviewStatusType;
    metadata?: Record<string, any>;
    version?: number;
    autoSave?: boolean;
}

export interface ReviewSectionForm {
    content_json?: Record<string, any>;
    review_status?: ReviewStatusType;
}

export interface AssignContentSourceForm {
    userId: string;
}

export interface ReorderSectionsForm {
    sections: Array<{
        id: string;
        order_index: number;
    }>;
}

// Export types
export interface ExportData {
    id: string;
    title: string;
    source: SourceType;
    metadata: Record<string, any> | null;
    sections: Array<{
        id: string;
        title: string;
        path: string;
        content: Record<string, any>;
        metadata: Record<string, any> | null;
        filter_tags: string[];
    }>;
}

// Permission types
export interface UserPermissions {
    role: UserRole;
    canCreateContentSource: boolean;
    canEditContentSource: boolean;
    canReviewContentSource: boolean;
    canDeleteContentSource: boolean;
    canAssignContentSource: boolean;
    canCreateSection: boolean;
    canEditSection: boolean;
    canReviewSection: boolean;
    canDeleteSection: boolean;
    canReorderSections: boolean;
}

// UI State types
export interface EditorState {
    currentSection: Section | null;
    isEditing: boolean;
    isReviewing: boolean;
    hasUnsavedChanges: boolean;
    autoSaveStatus: 'idle' | 'saving' | 'saved' | 'error';
    lastSavedAt?: Date;
}

export interface SectionTreeState {
    expandedNodes: Set<string>;
    selectedNode: string | null;
    loadingNodes: Set<string>;
}

export interface FilterState {
    search: string;
    status: string;
    filter_tags: string[];
    assigned_to_id: string;
    created_by_id: string;
    updated_by_id: string;
    visibility: string;
    review_status: string;
    sortBy: string;
    sortOrder: 'ASC' | 'DESC';
    page?: number;
    limit?: number;
}

// Hook return types
export interface UseContentSourcesReturn {
    contentSources: ContentSource[];
    loading: boolean;
    error: string | null;
    pagination: PaginationInfo | null;
    filters: FilterState;
    setFilters: (filters: Partial<FilterState>) => void;
    refetch: () => void;
    createContentSource: (data: CreateContentSourceForm) => Promise<ContentSource>;
    updateContentSource: (id: string, data: UpdateContentSourceForm) => Promise<ContentSource>;
    deleteContentSource: (id: string) => Promise<void>;
    assignContentSource: (id: string, data: AssignContentSourceForm) => Promise<void>;
    exportContentSource: (id: string) => Promise<ExportData>;
}

export interface UseSectionsReturn {
    sections: Section[];
    loading: boolean;
    error: string | null;
    refetch: () => void;
    createSection: (contentSourceId: string, data: CreateSectionForm) => Promise<Section>;
    updateSection: (id: string, data: UpdateSectionForm) => Promise<Section>;
    reviewSection: (id: string, data: ReviewSectionForm) => Promise<Section>;
    deleteSection: (id: string) => Promise<void>;
    reorderSections: (contentSourceId: string, data: ReorderSectionsForm) => Promise<void>;
}

export interface UseAutoSaveReturn {
    autoSaveStatus: 'idle' | 'saving' | 'saved' | 'error';
    lastSavedAt: Date | null;
    save: (data: any) => Promise<void>;
    reset: () => void;
    error: string | null;
    triggerAutoSave?: (data: any) => void;
    getStatusMessage?: () => string;
}

// Component props types
export interface ContentSourceCardProps {
    contentSource: ContentSource;
    onEdit: (id: string) => void;
    onReview: (id: string) => void;
    onAssign: (id: string) => void;
    onDelete: (id: string) => void;
    onExport: (id: string) => void;
    userPermissions: UserPermissions;
}

export interface SectionTreeNodeProps {
    section: SectionNode;
    level: number;
    onSelect: (section: Section) => void;
    onExpand: (sectionId: string) => void;
    onCollapse: (sectionId: string) => void;
    onAddChild: (parentId: string) => void;
    onDelete: (sectionId: string) => void;
    onReorder: (sectionId: string, newOrder: number) => void;
    userPermissions: UserPermissions;
    selectedSectionId?: string;
}

export interface MetadataPanelProps {
    contentSource?: ContentSource;
    section?: Section;
    onUpdate: (data: UpdateContentSourceForm | UpdateSectionForm) => void;
    userPermissions: UserPermissions;
    loading?: boolean;
}

export interface TipTapEditorProps {
    content: Record<string, any> | null;
    onChange: (content: Record<string, any>) => void;
    onSave?: (content: Record<string, any>) => void;
    autoSave?: boolean;
    readOnly?: boolean;
    placeholder?: string;
    className?: string;
}

// Error types
export interface ApiError {
    message: string;
    status: number;
    details?: any;
}

export interface ValidationError {
    field: string;
    message: string;
}

// Constants
export const CONTENT_STATUS_LABELS: Record<ContentStatus, string> = {
    draft: 'Draft',
    ready_for_review: 'Ready for Review',
    ethics_review: 'Ethics Review',
    ethics_review_in_process: 'Ethics Review in Process',
    ethics_reviewed: 'Ethics Reviewed',
    entity_extraction_in_process: 'Entity Extraction in Process',
    entity_reviewed: 'Entity Reviewed',
    published: 'Published'
};

export const VISIBILITY_LABELS: Record<VisibilityType, string> = {
    public: 'Public',
    karyakar: 'Karyakar',
    sant: 'Sant',
    hidden: 'Hidden'
};

export const REVIEW_STATUS_LABELS: Record<ReviewStatusType, string> = {
    none: 'None',
    escalate: 'Escalate',
    resolved: 'Resolved'
};

export const SOURCE_LABELS: Record<SourceType, string> = {
    book: 'Book',
    articles: 'Articles',
    website: 'Website',
    database: 'Database',
    'jivan charitra': 'Jivan Charitra'
};

export const INGEST_SOURCE_LABELS: Record<IngestSourceType, string> = {
    'indesign-html': 'InDesign HTML',
    'pagemaker': 'PageMaker',
    'word file': 'Word File',
    'database': 'Database',
    'excel': 'Excel'
};

export const USER_ROLE_LABELS: Record<UserRole, string> = {
    superadmin: 'Super Admin',
    admin: 'Admin',
    editor: 'Editor',
    reviewer: 'Reviewer',
    'editor-reviewer': 'Editor-Reviewer'
};

// Status colors for UI
export const CONTENT_STATUS_COLORS: Record<ContentStatus, string> = {
    draft: 'gray',
    ready_for_review: 'yellow',
    ethics_review: 'purple',
    ethics_review_in_process: 'orange',
    ethics_reviewed: 'green',
    entity_extraction_in_process: 'teal',
    entity_reviewed: 'blue',
    published: 'red'
};

export const REVIEW_STATUS_COLORS: Record<ReviewStatusType, string> = {
    none: 'gray',
    escalate: 'red',
    resolved: 'green'
};

export const VISIBILITY_COLORS: Record<VisibilityType, string> = {
    public: 'green',
    karyakar: 'blue',
    sant: 'purple',
    hidden: 'red'
};
